import * as React from "react";
import { Dialog as MUIDialog } from "@material-ui/core";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Slide from "@material-ui/core/Slide";
import { TransitionProps } from "@material-ui/core/transitions";
import { useCallback, useEffect } from "react";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      "& .MuiDialog-paperScrollPaper": {
        overflow: "hidden!important",
        padding: 0
      },
      position: "relative",
    },
    dialogHeader: {
      "& >div:first-child": {
        "& >p": {
          fontFamily: "Open Sans",
          fontStyle: "normal",
          fontWeight: 600,
          fontSize: "18px",
          lineHeight: "25px",
          display: "flex",
          alignItems: "center",
          color: "#212121",
          margin: 0
        }
      },
      background: "#FFFFFF",
      alignItems: "center",
      padding: "1.25rem 2.5rem",
      display: "flex",
      justifyContent: "space-between",
      borderBottom: "1px solid #E8EAF4"
    },
    body: {
      height: "calc(100% - 64px)",
      overflow: "auto",
      padding: "1.875rem 3.75rem",
      overflowX: "hidden",

      "@media(max-width: 960px)": {
        padding: "1.875rem 1rem",
      }
    },
  })
);

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement; },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export interface DialogProps {
  title?: string | JSX.Element;
  styles?: { className: string; };
  children: JSX.Element;
  isOpen: boolean;
  onClose: () => void;
  canClose?: boolean;
  isFormDialog?: boolean;
  maxWidth?: false | "md" | "xs" | "sm" | "lg" | "xl" | undefined;
  onCloseIcon?: () => void;
}

const Dialog: React.SFC<DialogProps> = ({
  isOpen,
  title,
  styles,
  onClose,
  children,
  canClose,
  isFormDialog,
  maxWidth,
  onCloseIcon
}) => {
  const classes = useStyles();
  /**Handles closing the dialog, saved as a callback so the reference doesnt change */
  const keyPress = useCallback((event: any) => {
    /**If escape key is pressed then close */
    if (event?.keyCode === 27) {
      onClose();
    }
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (isOpen) {
      console.log("setting event listener");
      window.addEventListener("keyup", keyPress);
    } else {
      console.log("removing event listener");
      window.removeEventListener("keyup", keyPress);
    }
    /**removes event listern on unmount */
    return () => {
      window.removeEventListener("keyup", keyPress);
    };
    //eslint-disable-next-line
  }, [isOpen]);

  function onClickIcon() {
    if (canClose) {
      if (onCloseIcon) {
        onCloseIcon();
      } else {
        onClose();
      }
    }
  }

  return (
    <MUIDialog
      // fullScreen
      TransitionComponent={Transition}
      onBackdropClick={canClose ? onClose : undefined}
      open={isOpen}
      maxWidth={maxWidth || "md"}
      className={`${classes.root} ${styles?.className}`}
      transitionDuration={300}
      data-testid="dialog-component"
      data-component-name="dialog"
    >
      {title && <header data-testid="dialog-appbar" className={classes.dialogHeader}>
        <div>
          <p>{title}</p>
        </div>
        <div>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onClickIcon}
            aria-label="close"
            data-component-name="DialogCloseButton"
          >
            <CloseIcon />
          </IconButton>
        </div>
      </header>}

      <div data-testid="dialog-children"
        style={isFormDialog ? { marginBottom: "5.5rem" } : undefined}
        className={`${classes.body} DialogBody`}>{children}</div>
    </MUIDialog>
  );
};

export default Dialog;
